import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './CommunityPage.css';
import { trackCommunityView, trackWhatsAppClick, trackFeatureClick } from '../utils/analytics';

function CommunityPage() {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('feed');

  const communitiesData = {
    indian: {
      name: 'Indian Students Association',
      flag: '🇮🇳',
      color: '#FF9933',
      members: 156,
      whatsapp: 'https://chat.whatsapp.com/indian-spu',
      description: 'Connecting Indian students at SPU. Cultural events, networking, and support.',
      events: ['Diwali Celebration', 'Holi Festival', 'Cricket Tournament'],
    },
    pakistani: {
      name: 'Pakistani Students Association',
      flag: '🇵🇰',
      color: '#01411C',
      members: 89,
      whatsapp: 'https://chat.whatsapp.com/pakistani-spu',
      description: 'Home away from home for Pakistani students at SPU.',
      events: ['Eid Celebration', 'Pakistan Day', 'Food Festival'],
    },
    brazilian: {
      name: 'Brazilian Students Association',
      flag: '🇧🇷',
      color: '#009C3B',
      members: 67,
      whatsapp: 'https://chat.whatsapp.com/brazilian-spu',
      description: 'Bringing Brazilian culture to SPU campus.',
      events: ['Carnaval Party', 'Brazilian BBQ', 'Samba Night'],
    },
    mexican: {
      name: 'Mexican Students Association',
      flag: '🇲🇽',
      color: '#006847',
      members: 78,
      whatsapp: 'https://chat.whatsapp.com/mexican-spu',
      description: 'Celebrating Mexican heritage and supporting students.',
      events: ['Dia de los Muertos', 'Cinco de Mayo', 'Taco Tuesday'],
    },
    african: {
      name: 'African Students Union',
      flag: '🌍',
      color: '#FFD700',
      members: 124,
      whatsapp: 'https://chat.whatsapp.com/african-spu',
      description: 'Representing the diversity of African nations at SPU.',
      events: ['African Night', 'Cultural Showcase', 'Heritage Month'],
    },
    chinese: {
      name: 'Chinese Students Association',
      flag: '🇨🇳',
      color: '#DE2910',
      members: 93,
      whatsapp: 'https://chat.whatsapp.com/chinese-spu',
      description: 'Supporting Chinese students and sharing culture.',
      events: ['Lunar New Year', 'Moon Festival', 'Dumpling Night'],
    },
    korean: {
      name: 'Korean Students Association',
      flag: '🇰🇷',
      color: '#003478',
      members: 45,
      whatsapp: 'https://chat.whatsapp.com/korean-spu',
      description: 'K-culture enthusiasts and Korean students community.',
      events: ['K-Pop Night', 'Korean BBQ', 'Chuseok Celebration'],
    },
    filipino: {
      name: 'Filipino Students Association',
      flag: '🇵🇭',
      color: '#0038A8',
      members: 72,
      whatsapp: 'https://chat.whatsapp.com/filipino-spu',
      description: 'Bayanihan spirit alive at SPU.',
      events: ['Barrio Fiesta', 'Independence Day', 'Karaoke Night'],
    },
    caribbean: {
      name: 'Caribbean Students Association',
      flag: '🌴',
      color: '#00CED1',
      members: 58,
      whatsapp: 'https://chat.whatsapp.com/caribbean-spu',
      description: 'Island vibes and Caribbean culture at SPU.',
      events: ['Carnival', 'Reggae Night', 'Beach Party'],
    },
    european: {
      name: 'European Students Association',
      flag: '🇪🇺',
      color: '#003399',
      members: 41,
      whatsapp: 'https://chat.whatsapp.com/european-spu',
      description: 'Connecting European students at SPU.',
      events: ['Europe Day', 'Language Exchange', 'Cultural Fair'],
    },
    arab: {
      name: 'Arab Students Association',
      flag: '🌙',
      color: '#006233',
      members: 63,
      whatsapp: 'https://chat.whatsapp.com/arab-spu',
      description: 'Arab culture and heritage at SPU.',
      events: ['Iftar Dinner', 'Arabic Night', 'Eid Celebration'],
    },
    latinx: {
      name: 'LatinX Students Association',
      flag: '🌎',
      color: '#8B4513',
      members: 112,
      whatsapp: 'https://chat.whatsapp.com/latinx-spu',
      description: 'Celebrating Latin American diversity at SPU.',
      events: ['Hispanic Heritage', 'Salsa Night', 'Latin Food Fest'],
    },
  };

  const community = communitiesData[communityId] || {
    name: 'Community',
    flag: '🌍',
    color: '#00eb79',
    members: 0,
    whatsapp: '',
    description: 'Student community at SPU',
    events: [],
  };

  // Track page view on mount
  useEffect(() => {
    trackCommunityView(communityId);
  }, [communityId]);

  const handleServiceClick = (serviceName, path) => {
    trackFeatureClick(serviceName, communityId);
    navigate(path);
  };

  const services = [
    { icon: '🚗', title: 'Rides', desc: 'Share rides with community', path: '/rides' },
    { icon: '📚', title: 'Tutoring', desc: 'Get help from peers', path: '/services?category=tutoring' },
    { icon: '🛒', title: 'Marketplace', desc: 'Buy & sell items', path: '/marketplace' },
    { icon: '🤝', title: 'Connect', desc: 'Find friends', path: '/community' },
  ];

  const posts = [
    { id: 1, author: 'Community Admin', avatar: community.flag, content: `Welcome to ${community.name}! Connect with fellow students here.`, time: '2 hours ago', likes: 24 },
    { id: 2, author: 'Student Member', avatar: '👤', content: 'Anyone going to the upcoming event? Looking for a ride!', time: '5 hours ago', likes: 12 },
    { id: 3, author: 'New Member', avatar: '👋', content: 'Just joined! Excited to connect with everyone.', time: '1 day ago', likes: 18 },
  ];

  const members = [
    { id: 1, name: 'Rahul S.', avatar: '👨‍🎓', role: 'President' },
    { id: 2, name: 'Priya M.', avatar: '👩‍🎓', role: 'Vice President' },
    { id: 3, name: 'Amit K.', avatar: '🧑‍🎓', role: 'Event Coordinator' },
    { id: 4, name: 'Sara T.', avatar: '👩‍🎓', role: 'Member' },
    { id: 5, name: 'Dev P.', avatar: '👨‍🎓', role: 'Member' },
  ];

  const openWhatsApp = () => {
    trackWhatsAppClick(communityId);
    window.open(community.whatsapp, '_blank');
  };

  return (
    <div className="community-detail-page">
      {/* Header Banner */}
      <div
        className="community-banner-header"
        style={{ background: `linear-gradient(135deg, ${community.color}, ${community.color}80)` }}
      >
        <button className="back-button" onClick={() => navigate('/peacock')}>
          ← Back
        </button>
        <div className="banner-content">
          <span className="banner-flag">{community.flag}</span>
          <h1>{community.name}</h1>
          <p>{community.members} members</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <button className="action-btn whatsapp" onClick={openWhatsApp}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Join WhatsApp
        </button>
        <button className="action-btn primary">
          ✓ Joined
        </button>
      </div>

      {/* About */}
      <div className="community-about">
        <p>{community.description}</p>
      </div>

      {/* Services Grid */}
      <div className="services-section">
        <h2>Community Services</h2>
        <div className="services-grid">
          {services.map((service, i) => (
            <div key={i} className="service-tile" onClick={() => handleServiceClick(service.title, service.path)}>
              <span className="service-icon">{service.icon}</span>
              <span className="service-title">{service.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="community-tabs">
        <button
          className={`tab ${activeTab === 'feed' ? 'active' : ''}`}
          onClick={() => setActiveTab('feed')}
        >
          📰 Feed
        </button>
        <button
          className={`tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          📅 Events
        </button>
        <button
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          👥 Members
        </button>
      </div>

      {/* Content */}
      <div className="community-content">
        {activeTab === 'feed' && (
          <div className="feed-section">
            {posts.map(post => (
              <div key={post.id} className="post-card">
                <div className="post-header">
                  <span className="post-avatar">{post.avatar}</span>
                  <div className="post-meta">
                    <span className="post-author">{post.author}</span>
                    <span className="post-time">{post.time}</span>
                  </div>
                </div>
                <p className="post-content">{post.content}</p>
                <div className="post-actions">
                  <button>❤️ {post.likes}</button>
                  <button>💬 Comment</button>
                  <button>🔗 Share</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'events' && (
          <div className="events-section">
            {community.events.map((event, i) => (
              <div key={i} className="event-card">
                <span className="event-icon">📅</span>
                <div className="event-info">
                  <h3>{event}</h3>
                  <span>Upcoming Event</span>
                </div>
                <button className="event-btn">RSVP</button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'members' && (
          <div className="members-section">
            {members.map(member => (
              <div key={member.id} className="member-card">
                <span className="member-avatar">{member.avatar}</span>
                <div className="member-info">
                  <h3>{member.name}</h3>
                  <span>{member.role}</span>
                </div>
                <button className="message-btn">💬</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CommunityPage;
